import React from "react";
import EnhancedTooltip, { type EnhancedTooltipTriggerVariant } from "../../components/EnhancedTooltip";
import StatusDot, { ALPHA_STATUS_TONE_CLASSES, type StatusTone } from "../../components/StatusDot";

export interface AlphaSettingWidgetHelpGuide {
  triggerLabel: string;
  title?: string;
  content: React.ReactNode;
  clickHint?: string;
  triggerVariant?: EnhancedTooltipTriggerVariant;
  placement?: "header" | "bottom-right";
  onClick: () => void;
}

export interface AlphaSettingWidgetProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  summary: string;
  subtext?: string;
  badge?: string;
  status?: {
    label: string;
    tone: StatusTone;
    pulse?: boolean;
  };
  isLoading?: boolean;
  error?: string | null;
  actionLabel?: string;
  actionDestructive?: boolean;
  onAction?: () => void;
  helpGuide?: AlphaSettingWidgetHelpGuide;
}

/**
 * v7 settings tile for the alpha profile page. Same props contract as the
 * legacy SettingWidget internal to SettingsPage, so widget wiring ports 1:1.
 */
const AlphaSettingWidget: React.FC<AlphaSettingWidgetProps> = ({
  icon,
  title,
  description,
  summary,
  subtext,
  badge,
  status,
  isLoading = false,
  error,
  actionLabel = "Manage",
  actionDestructive = false,
  onAction,
  helpGuide,
}) => {
  const helpTrigger = helpGuide ? (
    <EnhancedTooltip
      triggerLabel={helpGuide.triggerLabel}
      title={helpGuide.title}
      content={helpGuide.content}
      clickHint={helpGuide.clickHint}
      triggerVariant={helpGuide.triggerVariant ?? "icon"}
      onClick={helpGuide.onClick}
      maxWidthClass="max-w-sm"
      appearance="alpha"
    />
  ) : null;

  return (
    <div className="relative flex h-full min-h-[280px] flex-col rounded-v7-panel border border-v7-ink/15 bg-v7-white p-5 transition-colors duration-300 hover:bg-v7-paper md:p-6">
      <div className="flex flex-1 flex-col">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-v7-control bg-v7-paper text-v7-bronze-dark">
              {icon}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold leading-tight text-v7-ink">
                  {title}
                </h2>
                {helpGuide?.placement === "header" && helpTrigger}
                {status && <StatusDot {...status} appearance="alpha" />}
              </div>
              <p className="mt-1 text-sm text-v7-ink-soft">
                {description}
              </p>
            </div>
          </div>
          {badge && (
            <span className="shrink-0 rounded border border-v7-ink/15 bg-v7-ink/[0.04] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-v7-ink-soft">
              {badge}
            </span>
          )}
        </div>

        {error ? (
          <div className="rounded-v7-panel border border-v7-rust/30 bg-v7-rust/5 p-3 text-sm text-v7-rust">
            {error}
          </div>
        ) : isLoading ? (
          <div className="space-y-2">
            <div className="h-5 w-32 animate-pulse rounded bg-v7-mist" />
            <div className="h-4 w-48 animate-pulse rounded bg-v7-mist" />
          </div>
        ) : (
          <div className="flex flex-1 flex-col">
            <p className={`font-v7-serif text-lg ${status ? ALPHA_STATUS_TONE_CLASSES[status.tone].text : "text-v7-ink"}`}>
              {summary}
            </p>
            {subtext && (
              <p className="mt-1 line-clamp-2 text-xs text-v7-ink/60">
                {subtext}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="mt-5 flex min-h-[72px] items-end gap-3">
        {onAction && (
          <>
            <button
              type="button"
              onClick={onAction}
              className={
                actionDestructive
                  ? "inline-flex min-h-11 w-max items-center justify-center rounded-v7-control bg-v7-rust px-4 text-sm font-medium text-v7-white transition-colors hover:bg-v7-rust/90"
                  : "inline-flex min-h-11 w-max items-center justify-center rounded-v7-control bg-v7-ink px-4 text-sm font-medium text-v7-white transition-colors hover:bg-v7-ink-soft"
              }
            >
              {actionLabel}
            </button>
            {actionDestructive && (
              <span className="text-xs font-semibold uppercase tracking-wider text-v7-rust">
                Danger zone
              </span>
            )}
          </>
        )}
      </div>

      {helpGuide?.placement === "bottom-right" && (
        <div className="absolute bottom-5 right-5">
          {helpTrigger}
        </div>
      )}
    </div>
  );
};

export default AlphaSettingWidget;
